import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Beaker, SlidersHorizontal, Plug, Blocks, Rocket, Database, Zap, Upload, LineChart, Scaling, Users } from 'lucide-react';
import TopBar from '../../components/TopBar';
import PageTitle from '../../components/PageTitle';
import ContactFormModal from '../../components/ContactFormModal';
import SiteFooter from '../../components/SiteFooter';

const FINETUNE_URL = import.meta.env.VITE_FINETUNE_URL || 'https://ft.etherworks.cn';

const featureIcons = [Rocket, Database, Zap, Upload, LineChart, Scaling];

const methodDefs = [
  { icon: Beaker, key: 'sft', color: 'text-blue-500', bg: 'bg-blue-500/10', tags: ['LoRA', 'Full'] },
  { icon: SlidersHorizontal, key: 'dpo', color: 'text-violet-500', bg: 'bg-violet-500/10', tags: ['LoRA', 'Full'] },
  { icon: Plug, key: 'lora', color: 'text-green-500', bg: 'bg-green-500/10', tagKey: 'fine_tuning.methods_lora_tag' },
  { icon: Blocks, key: 'full', color: 'text-red-500', bg: 'bg-red-500/10', tagKey: 'fine_tuning.methods_full_tag' },
];

const modelProviders = [
  { name: 'Meta', models: 'Llama 4, Llama 3.3, Llama 3.1, CodeLlama', count: '30+', params: '1B – 1T MoE' },
  { name: 'DeepSeek', models: 'R1, V3, V3.1, R1-Distill', count: '10+', params: '1.5B – 671B MoE' },
  { name: 'Qwen', models: 'Qwen 3, Qwen 2.5, Qwen 2, Coder, VL', count: '30+', params: '0.6B – 480B MoE' },
  { name: 'Google', models: 'Gemma 3 (Base, IT, VLM)', count: '13', params: '270M – 27B' },
  { name: 'Mistral', models: 'Mixtral 8x7B, Mistral 7B', count: '4', params: '7B – 46.7B MoE' },
  { name: 'Moonshot', models: 'Kimi K2 (Thinking, Instruct, Base)', count: '4', params: '1T MoE' },
  { name: 'Zhipu AI', models: 'GLM-4.6, GLM-4.7', count: '2', params: '357B – 358B MoE' },
  { name: 'OpenAI', models: 'GPT-OSS', count: '2', params: '120B MoE' },
];

export default function FineTuning() {
  const { t } = useTranslation('products');
  const [showContact, setShowContact] = useState(false);

  const features = featureIcons.map((icon, i) => ({
    icon,
    title: t(`fine_tuning.feat${i + 1}_title`),
    desc: t(`fine_tuning.feat${i + 1}_desc`),
  }));

  return (
    <div className="min-h-screen bg-background">
      <PageTitle titleKey="fine_tuning.title" namespace="products" descriptionKey="fine_tuning.subtitle" />
      <TopBar />

      {/* Hero */}
      <section className="relative pt-24 pb-8 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/[0.03] via-transparent to-transparent dark:from-amber-500/[0.06]" />
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(245,158,11,0.07) 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute top-16 left-[10%] w-72 h-72 bg-amber-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-4 right-[8%] w-56 h-56 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 items-center justify-center mb-6 shadow-lg shadow-amber-500/20">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-4">
            <span className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 bg-clip-text text-transparent">
              {t('fine_tuning.name')}
            </span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground font-medium mb-8 max-w-2xl mx-auto">{t('fine_tuning.subtitle')}</p>

          <div className="flex justify-center flex-wrap gap-4 mb-10">
            <a
              href={`${FINETUNE_URL}/console/fine-tuning/new`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('fine_tuning.cta')}
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/plans"
              className="inline-flex items-center gap-2 px-8 py-3.5 border border-input bg-background hover:bg-accent hover:text-accent-foreground font-semibold rounded-xl transition-all"
            >
              {t('fine_tuning.view_pricing')}
            </Link>
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8 flex-wrap">
            {[
              { value: '100+', label: t('fine_tuning.stat_models') },
              { value: 'SFT + DPO', label: t('fine_tuning.stat_methods') },
              { value: 'LoRA', label: t('fine_tuning.stat_efficient') },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center">
                <span className="text-2xl font-bold text-foreground">{s.value}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Methods */}
      <section className="pt-8 pb-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('fine_tuning.methods_title')}</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">{t('fine_tuning.methods_subtitle')}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {methodDefs.map((m) => {
              const Icon = m.icon;
              const tags = m.tags || [t(m.tagKey!)];
              return (
                <div key={m.key} className="group relative rounded-[1.25rem] border border-border p-2 md:p-3 transition-transform hover:scale-[1.02]">
                  <div className="relative flex h-full flex-col gap-3 overflow-hidden rounded-xl border border-border bg-background p-6 shadow-sm dark:shadow-[0px_0px_27px_0px_rgba(45,45,45,0.3)]">
                    <div className="flex items-center gap-3">
                      <div className={`h-9 w-9 rounded-lg ${m.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${m.color}`} />
                      </div>
                      <h3 className="text-base font-semibold text-foreground tracking-tight">{t(`fine_tuning.methods_${m.key}_title`)}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(`fine_tuning.methods_${m.key}_desc`)}</p>
                    <div className="flex gap-2 mt-auto">
                      {tags.map((tag) => (
                        <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Supported Models */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('fine_tuning.models_title')}</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">{t('fine_tuning.models_subtitle')}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {modelProviders.map((p) => (
              <div key={p.name} className="rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-foreground">{p.name}</span>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{p.count} {t('fine_tuning.models_label')}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2 leading-relaxed">{p.models}</p>
                <span className="text-xs font-mono text-primary/70">{p.params}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-12 text-center">{t('fine_tuning.steps_title')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="text-center">
                <div className="inline-flex h-12 w-12 rounded-full bg-primary/10 items-center justify-center mb-4 text-xl font-bold text-primary">{step}</div>
                <h3 className="text-base font-semibold text-foreground mb-2">{t(`fine_tuning.step${step}_title`)}</h3>
                <p className="text-sm text-muted-foreground">{t(`fine_tuning.step${step}_desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('fine_tuning.features_title')}</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">{t('fine_tuning.features_subtitle')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-xl border border-border bg-card p-6 hover:border-primary/50 transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-3xl mx-auto text-center">
          <Users className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3">{t('fine_tuning.enterprise_title')}</h2>
          <p className="text-muted-foreground mb-8">{t('fine_tuning.enterprise_subtitle')}</p>
          <button
            onClick={() => setShowContact(true)}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-amber-500/25 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
          >
            {t('fine_tuning.contact_sales')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <SiteFooter />

      {showContact && <ContactFormModal isOpen={showContact} onClose={() => setShowContact(false)} product="fine-tuning" />}
    </div>
  );
}
